import { useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '@ebu/ui';
import { useI18n } from '../../i18n/i18n';
import { BroadcasterControls } from '../broadcaster-switch/BroadcasterControls';
import { LocalDataDialogButton } from '../guest/LocalDataPanel';
import { LIMIT_CHOICES } from './planner';
import { useScreenTime } from './screen-time';

/**
 * "For grown-ups" (ADR 0017): screen time for today, broadcaster and language, and "My data".
 * Opening it needs a 3-second press-and-hold — a step that keeps small children out, not a
 * security control (there are no child accounts to protect).
 */

const HOLD_MS = 3000;

export function GrownUpsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [unlocked, setUnlocked] = useState(false);
  const close = () => {
    setUnlocked(false);
    onClose();
  };
  return (
    <Dialog open={open} title={t('grown.title')} onCancel={close}>
      {unlocked ? <Settings onDone={close} /> : <HoldGate onUnlock={() => setUnlocked(true)} />}
    </Dialog>
  );
}

function HoldGate({ onUnlock }: { onUnlock: () => void }) {
  const { t } = useI18n();
  const [holding, setHolding] = useState(false);
  const timer = useRef(0);
  const start = () => {
    setHolding(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(onUnlock, HOLD_MS);
  };
  const stop = () => {
    setHolding(false);
    window.clearTimeout(timer.current);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <div className="grown-gate">
      <p>{t('grown.hold')}</p>
      <button
        type="button"
        className={holding ? 'hold-button is-holding' : 'hold-button'}
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
            e.preventDefault();
            start();
          }
        }}
        onKeyUp={stop}
        onBlur={stop}
      >
        <span className="hold-button__fill" aria-hidden="true" />
        <span className="hold-button__label">{t('grown.holdButton')}</span>
      </button>
    </div>
  );
}

function Settings({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const { state, setLimit } = useScreenTime();
  const [choice, setChoice] = useState<string>(
    state.limitMinutes === null ? 'none' : String(state.limitMinutes),
  );
  const used = Math.floor(state.usedSeconds / 60);
  const options = [
    { value: 'none', label: t('grown.noLimit') },
    ...LIMIT_CHOICES.map((m) => ({ value: String(m), label: t('grown.minutes', { minutes: m }) })),
  ];
  if (state.limitMinutes !== null && !options.some((o) => o.value === String(state.limitMinutes))) {
    options.push({
      value: String(state.limitMinutes),
      label: t('grown.minutes', { minutes: state.limitMinutes }),
    });
  }
  return (
    <form
      className="grown-settings"
      onSubmit={(e) => {
        e.preventDefault();
        setLimit(choice === 'none' ? null : Number(choice));
        onDone();
      }}
    >
      <fieldset className="grown-settings__limits">
        <legend>{t('grown.timeTitle')}</legend>
        <p className="grown-settings__intro">{t('grown.timeIntro')}</p>
        {options.map((o) => (
          <label key={o.value}>
            <input
              type="radio"
              name="screen-time"
              value={o.value}
              checked={choice === o.value}
              onChange={() => setChoice(o.value)}
            />
            {o.label}
          </label>
        ))}
        <p className="grown-settings__used">{t('grown.used', { minutes: used })}</p>
      </fieldset>
      <div className="grown-settings__section">
        <h3>{t('grown.settings')}</h3>
        <BroadcasterControls />
        <LocalDataDialogButton />
      </div>
      <div className="button-row">
        <Button type="submit">{t('grown.save')}</Button>
        <Button variant="secondary" onClick={onDone}>
          {t('grown.close')}
        </Button>
      </div>
    </form>
  );
}
